/* G3D custom (2026-10-10): the Pokedex collection page (sections/pokedex-page.liquid), direction A "House style".
   Ported from the approved, debloated mockup (Obsidian vault: Website/mockups/pokedex-a.html, after phase 2). Every ball,
   variant, price, stock state and photo comes from the #pd-data block the section renders from the collection; the type
   palette and marks, regions, group rules and the ready-made bundle table stay here, as in the mockup.
   Add to cart goes through assets/g3d-cart.js, which fires the theme's CartLinesUpdateEvent, so
   the header bubble and the cart drawer refresh. Module script: nothing lands on window. */
import { addToCart } from '@theme/g3d-cart';
import { formatMoney } from '@theme/money-formatting';

const root = document.querySelector('.g3d-pd');
const $ = id => document.getElementById('pd-' + id);
const DATA = JSON.parse($('data').textContent);
const LANG = document.documentElement.lang || 'en';
const CUR = { style: 'currency', currency: DATA.currency || 'USD' };
const whole = new Intl.NumberFormat(LANG, { ...CUR, minimumFractionDigits: 0, maximumFractionDigits: 0 });
const money = cents => formatMoney(cents, DATA.moneyFormat || '${{amount}}', CUR.currency); // prices come in cents, in the shopper's currency; as Liquid's | money
const range = ([lo, hi]) => lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`;
const img = (url, w) => url.replace(/width=\d+/, 'width=' + w); // the section hands out 1000px image URLs
// the detail's photo strip, [view, url] pairs: each view is the first photo whose alt names it ("Charizard Ball - Shiny left");
// a ball whose front is a showcase shot ("Gen 3 - Torchic (photographed)") uses its featured photo there
const VIEWS = ['front', 'left', 'right', 'back'];
const viewsOf = (media, shiny, front) => VIEWS.map((v, k) => [v, media.find(([alt]) => /shiny/i.test(alt) === shiny
  && new RegExp(`\\b${v}\\b`, 'i').test(alt))?.[1] || (k ? null : front)]).filter(x => x[1]);
// a poncho pick shows that poncho's own photos ("Vaporeon - front (photographed)"), and Umbreon Ball's Pikachu-base poncho its
// "Umbreon - ..." ones; empty when that poncho has no photos yet, so the caller falls back to the product's first set
const ponchoViews = (p, sel) => {
  const pre = (sel.Eeveelution ? sel.Eeveelution + ' - ' : /Pikachu/.test(sel.Type || '') ? p.title + ' - ' : '').toLowerCase();
  if (!pre) return [];
  p.pv = p.pv || {};
  return p.pv[pre] = p.pv[pre] || viewsOf(p.media.filter(([alt]) => (alt || '').toLowerCase().startsWith(pre)), false, null);
};
// most balls share these; the store's dex_facts metafield fills the rest
const DEX = DATA.balls.map(b => {
  // the card's price is one the shopper can pay: in-stock variants only (all of them when none is in stock)
  const f = b.facts || {}, sale = b.variants.filter(v => v[3]), prices = (sale.length ? sale : b.variants).map(v => v[2]);
  // the featured photo is the first one (the section skips balls without a photo); the shiny one, the first whose alt says shiny and front
  const front = b.media[0][1], shiny = b.media.find(([a]) => /shiny/i.test(a) && /front/i.test(a))?.[1] || null;
  return { name: b.title.replace(/ Ball$/, ''), full: b.title, handle: b.handle, url: b.url, num: b.num, types: f.types || [], category: f.category ?? null,
    height: f.height ?? null, weight: f.weight ?? null, desc: b.desc || null, groups: b.tags, opt: b.options.map(o => o[0]),
    options: Object.fromEntries(b.options), variants: b.variants, price: [Math.min(...prices), Math.max(...prices)], front, shinyFront: shiny,
    views: viewsOf(b.media, false, front !== shiny && front), shinyViews: viewsOf(b.media, true, shiny), media: b.media }; // Haxorus: shiny photos only
});

// our own type palette and marks (simple shapes drawn for this page; "!" = even-odd fill for shapes with holes)
const TYPE_C = { Normal: '#9c9c8a', Fire: '#f06a24', Water: '#2f8fe0', Grass: '#3fa34d', Electric: '#f2c318', Ice: '#5fc7e3', Fighting: '#c43d2b',
  Poison: '#9b4fc4', Ground: '#cf9550', Flying: '#7f9cec', Psychic: '#ee4f86', Bug: '#93ad1e', Rock: '#ad995b', Ghost: '#6a54a6', Dragon: '#5661e0',
  Dark: '#4f4654', Steel: '#6b97ab', Fairy: '#e88bda' };
const ICON = {
  Normal: '!M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 5a4 4 0 1 1 0 8 4 4 0 0 1 0-8z',
  Fire: 'M12 2c1 4 6 6 6 12a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-4-1-6 1-9z',
  Water: 'M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13z',
  Grass: 'M20 4C9 4 4 10 4 17c0 1 .1 2 .3 3C6 13 11 9 16 8c-5 3-8 7-9.5 12.5.8.3 1.7.5 2.5.5C17 21 20 13 20 4z',
  Electric: 'M14 2 4 14h6.5L9 22l11-13h-7z',
  Ice: 'M10.8 2h2.4v20h-2.4zM3.94 5.96l17.32 10-1.2 2.08-17.32-10zM2.74 15.96l17.32-10 1.2 2.08-17.32 10z',
  Fighting: '!M5 10a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v3a8 8 0 0 1-8 8h-1a6 6 0 0 1-6-6zM9.2 7v4h1.4V7zm4.2 0v4h1.4V7z',
  Poison: 'M12 2a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM6.5 15.5a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM17 16a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  Ground: 'M3 12h18v3.2H3zM5.5 17h13v3.2h-13zM12 3l7 7H5z',
  Flying: 'M2 14c6 1 11-2 20-10-1 9-6 15-13 15-3 0-5-2-7-5z',
  Psychic: '!M12 5C6 5 2 12 2 12s4 7 10 7 10-7 10-7-4-7-10-7zm0 3.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7zm0 2a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z',
  Bug: 'M12 7.5c3.3 0 5.5 3.2 5.5 6.8S15.3 21 12 21s-5.5-3.1-5.5-6.7S8.7 7.5 12 7.5zM8.6 2.6l1.3-.7 2 3.4-1.3.7zM15.4 2.6l-1.3-.7-2 3.4 1.3.7z',
  Rock: 'M4 19 5.5 9.5 11 5l7 2 3 7.5L18 19z',
  Ghost: '!M5 21V11a7 7 0 0 1 14 0v10l-2.33-2-2.34 2L12 19l-2.33 2-2.34-2zM8.8 10h2.2v3H8.8zm4.2 0h2.2v3H13z',
  Dragon: 'M5 21c0-9 5-16 15-18-4 3-6 7-6 11 0 4-3 7-9 7z',
  Dark: 'M14.5 3a9 9 0 1 0 6.5 15.2A8 8 0 0 1 14.5 3z',
  Steel: '!M12 2l8.66 5v10L12 22l-8.66-5V7zm0 6.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z',
  Fairy: 'M12 2c.9 5.6 4.4 9.1 10 10-5.6.9-9.1 4.4-10 10-.9-5.6-4.4-9.1-10-10 5.6-.9 9.1-4.4 10-10z'
};
// generations by dex number; a ball with no number (Browt) is in the newest region, Gen 10 "Winds & Waves".
// The section repeats these bounds for the hero numbers.
const REGIONS = [['Kanto', 151], ['Johto', 251], ['Hoenn', 386], ['Sinnoh', 493], ['Unova', 649], ['Kalos', 721], ['Alola', 809], ['Galar', 905], ['Paldea', 1025], ['Winds & Waves', 0]]
  .map(([name, hi], i, a) => ({ name, gen: i + 1, lo: i ? a[i - 1][1] + 1 : 1, hi }));
const NEWEST = REGIONS.length - 1;
// a form is named by its first word ("Origin Giratina"); it shares its base ball's dex number
const FORM = /^(Mega|Primal|Shadow|Origin|Alolan|Crowned|Midnight) /;
// [id, label, test]; groups are the products' tags
const GROUPS = [
  ['starter', 'Starters', p => p.groups.includes('starter')],
  ['legendary', 'Legendaries', p => p.groups.includes('legendary')],
  ['mythical', 'Mythicals', p => p.groups.includes('mythical')],
  ['eeveelution', 'Eeveelutions', p => p.groups.includes('eeveelution')],
  ['spooky', 'Spooky', p => p.groups.includes('spooky')],
  ['forms', 'Forms', p => FORM.test(p.name)],
  // the poncho products only; Umbreon Ball's "Poncho - ..." versions don't count (owner, 2026-10-15)
  ['poncho', 'Ponchos', p => /Poncho/.test(p.name)],
  ['shinyphoto', 'Shiny photo', p => !!p.shinyFront]
].map(([id, label, test]) => ({ id, label, test }));
// Size before Function: an option is greyed by the ones shown before it, so 100mm stays pickable and greys out Clicker
const OPT_ORDER = ['Style', 'Size', 'Function', 'Form', 'Type', 'Eeveelution'];
const OPT_LABEL = { Type: 'Version', Eeveelution: 'Poncho' }; // "Type" would read as the Pokemon's type here
// a ball's ready-made bundle by exact name (the bundle pages' member tables), else Build-your-own when the ball is in the
// Build-your-own collection (section setting); [chip text, which link from the section settings]
const BUNDLE = {};
'Bulbasaur Charmander Squirtle|Chikorita Cyndaquil Totodile|Treecko Torchic Mudkip|Turtwig Chimchar Piplup|Snivy Tepig Oshawott|Chespin Fennekin Froakie|Rowlet Litten Popplio|Grookey Scorbunny Sobble|Sprigatito Fuecoco Quaxly|Browt Pombon Gecqua'
  .split('|').forEach((t, i) => t.split(' ').forEach(n => BUNDLE[n] = [`In the Gen ${i + 1} Starter Trio`, 'starter']));
Object.entries({ 'Legendary Birds': 'Articuno Zapdos Moltres', 'Legendary Beasts': 'Raikou Entei Suicune', 'Regi Trio': 'Regirock Regice Registeel',
  'Weather Trio': 'Kyogre Rayquaza Groudon', 'Lake Trio': 'Uxie Azelf Mesprit', 'Creation Trio': 'Dialga Giratina Palkia',
  'Tao Trio': 'Reshiram Zekrom Kyurem', 'Aura Trio': 'Xerneas Yveltal Zygarde', 'Light Trio': 'Solgaleo Necrozma Lunala' })
  .forEach(([t, ns]) => ns.split(' ').forEach(n => BUNDLE[n] = [`In the ${t} bundle`, 'legendary']));
'Eevee Vaporeon Jolteon Flareon Espeon Umbreon Leafeon Glaceon Sylveon'.split(' ').forEach(n => BUNDLE[n] = ['In the Eeveelution Bundle', 'eeveelution']);
const BYO = new Set(DATA.byo);
const dealOf = p => BUNDLE[p.name] || (BYO.has(p.handle) ? ['Mix any 3, $7 off', 'byo'] : null);

const pad = n => String(n).padStart(3, '0');
const dexNo = p => 'No.' + (p.num == null ? '???' : pad(p.num));
const byHandle = h => DEX.find(p => p.handle === h);
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const plural = (n, w) => n + ' ' + w + (n === 1 ? '' : 's');
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const replay = (el, cls) => { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); };
// readable text on a type colour: whichever of white / ink has the higher WCAG contrast
const lum = hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(c => c <= .04045 ? c / 12.92 : ((c + .055) / 1.055) ** 2.4)
  .reduce((s, c, i) => s + c * [.2126, .7152, .0722][i], 0);
const onColour = hex => 1.05 / (lum(hex) + .05) >= (lum(hex) + .05) / (lum('#16161a') + .05) ? '#fff' : '#16161a';
const ON = Object.fromEntries(Object.entries(TYPE_C).map(([t, c]) => [t, onColour(c)]));
const icon = t => { const d = ICON[t]; return `<svg viewBox="0 0 24 24" aria-hidden="true"><path${d[0] === '!' ? ' fill-rule="evenodd"' : ''} d="${d.replace('!', '')}"/></svg>`; };
const typePill = t => TYPE_C[t] ? `<span class="pd-type" style="--c:${TYPE_C[t]};--on:${ON[t]}">${icon(t)}${t}</span>` : '<span class="pd-type pd-type--unk">???</span>';

/* ---- prepare: forms, regions, colours, search text; then dex order (the store's manual order has slips) ---- */
DEX.forEach(p => {
  p.form = p.name.match(FORM)?.[1] || (/Poncho/.test(p.name) ? 'Poncho' : p.num == null ? 'New' : '');
  const m = p.name.match(/^(\w+)(?: -)? (.*Poncho)$/); // "Pikachu - Eeveelutions Poncho" -> Pikachu / Eeveelutions Poncho
  p.title = m ? m[1] : p.name;
  p.sub = m ? m[2] : p.num == null ? 'Newest discovery' : '';
  const known = p.types.filter(t => TYPE_C[t]);
  p.c = TYPE_C[known[0]] || '#8b8d98';
  p.c2 = TYPE_C[known[1]] || p.c;
  const r = p.num == null ? NEWEST : REGIONS.findIndex(r => r.hi && p.num <= r.hi);
  p.region = r < 0 ? NEWEST : r;
});
const RANK = { '': 0, New: 0, Mega: 1, Primal: 1, Shadow: 1, Poncho: 2 };
DEX.sort((a, b) => (a.num ?? 1e4) - (b.num ?? 1e4) || (RANK[a.form] ?? 1) - (RANK[b.form] ?? 1) || a.name.localeCompare(b.name));
DEX.forEach((p, i) => {
  p.i = i;
  p.forms = DEX.filter(q => q !== p && q.num != null && q.num === p.num);
  p.hay = [p.name, dexNo(p), ...p.types, p.category || '', REGIONS[p.region].name, p.form,
    ...GROUPS.filter(g => g.test(p)).flatMap(g => [g.id, g.label])].join(' ').toLowerCase();
});

// the store variant for the chosen options (none = that combination isn't offered); [id, option values, cents, available]
const variantOf = (p, sel) => p.variants.find(v => p.opt.every((k, i) => v[1][i] === sel[k]));

/* ---- state: the dropdowns, the search and the look all write it; apply() shows the matching cards ---- */
const state = { region: -1, group: '', type: '', q: '', shiny: false };
const grid = $('grid'), regionsEl = $('regions'), groupsEl = $('groups'), typesEl = $('types'), q = $('q');
let visible = [];

/* ---- filter options ---- */
const inRegion = ri => DEX.filter(p => p.region === ri);
regionsEl.innerHTML = REGIONS.map((r, ri) =>
  `<button type="button" class="pd-pill" data-r="${ri}" aria-label="${r.name}, Gen ${r.gen}, ${plural(inRegion(ri).length, 'ball')}">${r.name}<small>${inRegion(ri).length}</small></button>`).join('');
groupsEl.innerHTML = GROUPS.map(g => `<button type="button" class="pd-pill" data-g="${g.id}">${g.label}<small>${DEX.filter(g.test).length}</small></button>`).join('');
typesEl.innerHTML = Object.keys(TYPE_C).map(t => {
  const n = DEX.filter(p => p.types.includes(t)).length;
  return n ? `<button type="button" class="pd-pill" data-t="${t}" style="--c:${TYPE_C[t]}"><i class="pd-dot"></i>${t}<small>${n}</small></button>` : '';
}).join('');

/* ---- the grid: built once, filtering only hides cards (photos already loaded stay loaded) ---- */
const tagFor = p => p.form === 'New' ? '<span class="pd-card__tag pd-card__tag--new">New</span>' : p.form ? `<span class="pd-card__tag">${p.form}</span>` : '';
// a real link to the product page (crawlers, new-tab clicks); a plain click opens the detail instead (grid.onclick)
const cardHTML = p => `<li><a class="pd-card${p.num == null ? ' pd-is-unknown' : ''}" href="${esc(p.url)}" data-i="${p.i}" style="--c:${p.c};--c2:${p.c2}" aria-haspopup="dialog"
  aria-label="${esc(p.full)}, ${p.num == null ? 'number unknown' : 'number ' + p.num}${p.form && p.form !== 'New' ? ', ' + p.form + ' form' : ''}, ${p.types.filter(t => TYPE_C[t]).join(' and ') || 'type unknown'}, ${p.price[0] < p.price[1] ? 'from ' : ''}${money(p.price[0])}">
  <i class="pd-foil" aria-hidden="true"></i>
  <span class="pd-card__top"><b>${esc(p.title)}</b><span>${dexNo(p)}</span></span>
  <span class="pd-card__ph">${tagFor(p)}<img src="${img(p.front, 360)}" alt="${esc(p.full)}" loading="lazy" decoding="async" width="360" height="360"><span class="pd-card__badge">✦ Shiny photo</span><span class="pd-card__plain">Normal photo</span><i class="pd-sweep"></i>${p.num == null ? '<i class="pd-scan"></i>' : ''}</span>
  ${p.sub ? `<span class="pd-card__sub">${esc(p.sub)}</span>` : ''}
  <span class="pd-types">${p.types.map(typePill).join('')}</span>
  <span class="pd-card__foot"><span class="pd-card__price">${p.price[0] < p.price[1] ? 'from' : ''}<b>${money(p.price[0])}</b></span><span class="pd-card__go" aria-hidden="true">→</span></span>
</a></li>`;
// a region's type mix: one arc per type, most common first (dashed grey while no type is known)
function ring(ps) {
  const n = {};
  ps.forEach(p => p.types.forEach(t => { if (TYPE_C[t]) n[t] = (n[t] || 0) + 1; }));
  const mix = Object.entries(n).sort((a, b) => b[1] - a[1]), total = mix.reduce((s, m) => s + m[1], 0);
  let at = 0;
  const arcs = mix.map(([t, k]) => { const len = k / total * 100, d = Math.max(len - 1.6, .6), arc = `<circle r="42" pathLength="100" stroke="${TYPE_C[t]}" stroke-dasharray="${d} ${100 - d}" stroke-dashoffset="${-at}"/>`; at += len; return arc; }).join('')
    || '<circle r="42" pathLength="100" stroke="var(--muted)" stroke-opacity=".5" stroke-dasharray="2 2.17"/>';
  const label = mix.length ? 'Type mix: ' + mix.map(([t, k]) => `${t} ${k}`).join(', ') : 'No known types yet';
  return `<svg class="pd-ring" viewBox="-50 -50 100 100" role="img"><title>${label}</title>
    <g class="pd-ring__mix"><g fill="none" stroke-width="8" transform="rotate(-90)">${arcs}</g></g>
    <path d="M-28 0a28 28 0 0 1 56 0z" fill="${mix.length ? TYPE_C[mix[0][0]] : 'var(--surface)'}"/><path d="M-28 0a28 28 0 0 0 56 0z" fill="#f4f4f4"/>
    <g fill="none" stroke="#111"><circle r="28" stroke-width="4"/><path d="M-28 0h18M10 0h18" stroke-width="5"/><circle r="10" fill="#f4f4f4" stroke-width="4"/></g></svg>`;
}
const tabHTML = (r, ri, ps) => `<li class="pd-divider" data-r="${ri}"><div class="pd-tab${ps.length ? '' : ' pd-is-empty'}">
  <span class="pd-tab__gen" aria-hidden="true">${String(r.gen).padStart(2, '0')}</span>
  <span class="pd-eyebrow"><span class="pd-pball"></span>Gen ${r.gen}${ri === NEWEST ? '<span class="pd-tab__new">NEW</span>' : ''}</span>
  <span class="pd-tab__body">${ring(ps)}
    <h3>${r.name}</h3>
    <span class="pd-tab__meta">${r.hi ? `No.${pad(r.lo)}–${pad(r.hi)}` : 'The newest region'} · <b></b></span>
    ${ps.length ? '' : `<span class="pd-tab__ask">Nothing from Gen ${r.gen} on the shelf yet. <a href="${esc(DATA.links.custom)}">Request one <span aria-hidden="true">→</span></a></span>`}</span>
</div></li>`;
grid.innerHTML = `<ul class="pd-grid">${REGIONS.map((r, ri) => tabHTML(r, ri, inRegion(ri)) + inRegion(ri).map(cardHTML).join('')).join('')}</ul>`;
const cards = [...grid.querySelectorAll('.pd-card')];
const sections = REGIONS.map((r, ri) => ({ el: grid.querySelector(`.pd-divider[data-r="${ri}"]`), n: grid.querySelector(`.pd-divider[data-r="${ri}"] .pd-tab__meta b`),
  lis: cards.filter(c => DEX[+c.dataset.i].region === ri).map(c => c.parentElement), empty: !inRegion(ri).length }));
const cardOf = i => cards.find(c => +c.dataset.i === i);

function matches(p, words) {
  if (state.group && !GROUPS.find(g => g.id === state.group).test(p)) return false;
  if (state.type && !p.types.includes(state.type)) return false;
  // a number ("25", "#025", "no.25") matches that dex number; words must all appear in the name/type/group/region text
  return words.every(w => /^\d+$/.test(w) ? p.num === +w : p.hay.includes(w));
}

function apply(deal) {
  const words = state.q.toLowerCase().replace(/#|no\.\s*/g, ' ').split(/\s+/).filter(Boolean).map(w => /^\d+$/.test(w) ? String(+w) : w);
  const bare = !state.group && !state.type && !words.length;
  visible = [];
  sections.forEach((s, ri) => {
    const inView = state.region < 0 || state.region === ri;
    let n = 0;
    s.lis.forEach(li => { const p = DEX[+li.firstElementChild.dataset.i], ok = inView && matches(p, words); li.hidden = !ok; if (ok) { n++; visible.push(p.i); } });
    s.el.hidden = !n && !(s.empty && inView && bare); // an empty region's tab shows only while nothing else is filtered
    s.n.textContent = s.empty ? 'none yet' : n === s.lis.length ? plural(n, 'ball') : `${n} of ${s.lis.length}`;
  });
  const on = { region: state.region >= 0 && REGIONS[state.region].name, group: state.group && GROUPS.find(g => g.id === state.group).label, type: state.type };
  const empty = !visible.length && !(bare && state.region >= 0 && !inRegion(state.region).length); // an empty region shows its own "none yet" tab
  $('empty').hidden = !empty;
  if (empty) {
    const bits = [state.q && `"${state.q.trim()}"`, on.type, on.group, on.region].filter(Boolean);
    $('emptyH').textContent = 'No balls match ' + (bits.join(' + ') || 'that');
    $('emptyP').textContent = state.region >= 0 && !inRegion(state.region).length ? `Nothing from ${REGIONS[state.region].name} on the shelf yet, so there's nothing to filter.`
      : 'Try another name, number or type, or clear the filters to see all ' + DEX.length + ' balls.';
  }
  const filtered = state.region >= 0 || !bare;
  $('count').innerHTML = filtered ? `Showing <b>${visible.length}</b> of ${DEX.length} balls` : `All <b>${DEX.length}</b> balls, in dex order`;
  $('clear').hidden = !filtered;
  $('qClear').hidden = !state.q;
  $('qKey').hidden = !!state.q;
  // the options and the three triggers mirror the state (the search box shows its own)
  regionsEl.querySelectorAll('.pd-pill').forEach(b => b.setAttribute('aria-pressed', +b.dataset.r === state.region));
  groupsEl.querySelectorAll('.pd-pill').forEach(b => b.setAttribute('aria-pressed', b.dataset.g === state.group));
  typesEl.querySelectorAll('.pd-pill').forEach(b => b.setAttribute('aria-pressed', b.dataset.t === state.type));
  root.querySelectorAll('.pd-dd').forEach(dd => {
    const v = on[dd.dataset.k], b = dd.firstElementChild;
    dd.classList.toggle('pd-is-set', !!v);
    b.firstElementChild.classList.toggle('pd-sr', !!v); // the value replaces the label on screen; a screen reader hears "Region Kanto"
    b.children[1].innerHTML = !v ? '' : (dd.dataset.k === 'type' ? `<i class="pd-dot" style="--c:${TYPE_C[v]}"></i>` : '') + v;
    dd.lastElementChild.hidden = !v;
  });
  // deal the matching cards in again: one reflow for all of them, not one per card
  if (deal && !REDUCED) {
    const lis = [...grid.firstElementChild.children].filter(li => !li.hidden);
    lis.forEach(li => li.classList.remove('pd-is-in'));
    void grid.offsetWidth;
    lis.forEach((li, k) => { li.style.setProperty('--d', Math.min(k, 14) * 28 + 'ms'); li.classList.add('pd-is-in'); });
  }
}
function set(patch) { Object.assign(state, patch); apply(true); }

regionsEl.onclick = e => { const b = e.target.closest('.pd-pill'); if (b) set({ region: state.region === +b.dataset.r ? -1 : +b.dataset.r }); };
groupsEl.onclick = e => { const b = e.target.closest('.pd-pill'); if (b) set({ group: state.group === b.dataset.g ? '' : b.dataset.g }); };
typesEl.onclick = e => { const b = e.target.closest('.pd-pill'); if (b) set({ type: state.type === b.dataset.t ? '' : b.dataset.t }); };
q.oninput = () => { state.q = q.value; apply(false); }; // typing filters without replaying the deal-in
q.onkeydown = e => { if (e.key === 'Escape' && q.value) { e.preventDefault(); q.value = ''; set({ q: '' }); } else if (e.key === 'Enter') q.blur(); }; // Enter (the phone's Search key) puts the keyboard away so the results show
$('qClear').onclick = () => { q.value = ''; set({ q: '' }); q.focus(); };
const clearAll = () => { q.value = ''; set({ region: -1, group: '', type: '', q: '' }); };
$('clear').onclick = () => { clearAll(); $('dds').querySelector('.pd-dd__btn').focus(); }; // it hides itself, so focus moves to the first dropdown
$('emptyClear').onclick = () => { clearAll(); q.focus(); };
/* ---- the dropdowns: a disclosure each, one open at a time; a pick, Escape or a click outside closes it ---- */
let ddOpen = null; // the trigger whose popover is open
function closeDd(focus) {
  if (!ddOpen) return;
  ddOpen.setAttribute('aria-expanded', false);
  ddOpen.nextElementSibling.hidden = true;
  if (focus) ddOpen.focus({ preventScroll: true }); // a click far down the page must not jump back up to the toolbar
  ddOpen = null;
}
// focus moving to any control outside the open dropdown (Tab, "/", a click on another control) closes it; not focus pulled into a
// non-modal dialog (the cart drawer docked open from 990px traps focus, which would close the popover the moment it opens)
$('dds').addEventListener('focusout', e => { if (ddOpen && e.relatedTarget && !ddOpen.parentElement.contains(e.relatedTarget) && !e.relatedTarget.closest('dialog:not(:modal)')) closeDd(); });
$('dds').onclick = e => {
  const b = e.target.closest('.pd-dd__btn'), x = e.target.closest('.pd-dd__x');
  if (b) {
    const was = b === ddOpen;
    closeDd();
    if (was) return;
    ddOpen = b;
    b.setAttribute('aria-expanded', true);
    b.nextElementSibling.hidden = false;
    (b.nextElementSibling.querySelector('[aria-pressed=true]') || b.nextElementSibling.firstElementChild).focus();
  }
  if (e.target.closest('.pd-pill')) closeDd(true); // the popover's own handler has already applied the pick
  if (x) { const k = x.parentElement.dataset.k; set({ [k]: k === 'region' ? -1 : '' }); x.parentElement.firstElementChild.focus(); }
};
// ponytail: listens on this section only, so a click on a non-focusable spot in the theme header leaves a popover open (Tab, Escape or any click here closes it)
root.addEventListener('click', e => {
  const a = document.activeElement;
  // focus goes back to the trigger unless the click put it on another control
  if (ddOpen && !ddOpen.parentElement.contains(e.target)) closeDd(a === document.body || ddOpen.parentElement.contains(a));
});
root.addEventListener('keydown', e => { if (e.key === 'Escape' && ddOpen) { e.preventDefault(); closeDd(true); } });
// "/" jumps to the search, like most catalogues: the one page-wide listener, and never while typing somewhere, inside a dialog or
// with a modal dialog open (Horizon's cart drawer docked open from 990px up is a non-modal dialog[open], so it doesn't count)
document.addEventListener('keydown', e => {
  if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
  const t = e.composedPath()[0];
  if (t instanceof Element && (t.closest('input, textarea, select, [contenteditable]') || t.isContentEditable)) return;
  if (document.querySelector('dialog:modal') || (t instanceof Element && t.closest('dialog'))) return;
  e.preventDefault();
  q.focus();
});

/* ---- Normal / Shiny: gold frames everywhere; real shiny photos swap in, the rest say they show the normal print ---- */
const shownPhoto = (p, shiny) => (shiny && p.shinyFront) || p.front;
function applyLook(sweep) {
  const sh = state.shiny;
  grid.classList.toggle('pd-is-shiny', sh);
  cards.forEach(c => {
    const p = DEX[+c.dataset.i], f = shownPhoto(p, sh), im = c.querySelector('.pd-card__ph img'), src = img(f, 360);
    if (im.getAttribute('src') !== src) { im.src = src; im.alt = (f === p.shinyFront ? 'Shiny ' : '') + p.full; }
    c.classList.toggle('pd-is-shinyphoto', f === p.shinyFront);
    c.classList.toggle('pd-is-plainphoto', sh && !p.shinyFront);
    c.classList.remove('pd-is-swept');
  });
  if (sh && sweep && !REDUCED) {
    void grid.offsetWidth;
    cards.filter(c => c.classList.contains('pd-is-shinyphoto')).forEach((c, k) => { c.style.setProperty('--d', k * 120 + 'ms'); c.classList.add('pd-is-swept'); });
  }
  const n = DEX.filter(p => p.shinyFront).length;
  $('shinyNote').hidden = !sh;
  $('shinyNoteT').textContent = `Shiny on. ${n} balls have a real shiny photo (marked ✦ Shiny photo); the rest still show their normal print, marked "Normal photo", until theirs is taken. Every ball can be ordered Shiny at the same price.`;
}
$('look').onchange = e => { state.shiny = e.target.value === 'Shiny'; applyLook(true); };

/* ---- detail ---- */
const dlg = $('dlg'), big = $('big'), dImg = $('dImg'), atc = $('atc'), views = $('dViews');
const VIEW_LABEL = { front: 'Front', left: 'Left', right: 'Right', back: 'Back' };
let list = [], at = 0, cur, sel = {}, opener, addedTimer, busy = false, view = 'front', strip;
dImg.onload = dImg.onerror = () => dImg.classList.remove('pd-is-loading');

function open(i, from, all) {
  opener = from;
  sel = {};
  list = all ? DEX.map(p => p.i) : visible.includes(i) ? visible.slice() : [i];
  show(list.indexOf(i));
  document.documentElement.classList.add('pd-lock'); // page scroll lock (pokedex-page.css)
  dlg.showModal();
  dlg.scrollTop = 0;
  // grow out of whatever was clicked
  const c = from.getBoundingClientRect(), b = big.getBoundingClientRect();
  big.style.transformOrigin = `${c.left + c.width / 2 - b.left}px ${c.top + c.height / 2 - b.top}px`;
  replay(big, 'pd-is-in');
}
function show(k) {
  at = (k + list.length) % list.length;
  cur = DEX[list[at]];
  view = 'front'; // every ball opens on its front photo
  const p = cur;
  // Style carries over while you step through balls; it starts from the page's Normal/Shiny switch. The other options
  // start on the first variant in stock in that style (the first variant when none is).
  const want = sel.Style || (state.shiny ? 'Shiny' : 'Normal'), style = (p.options.Style || [want]).includes(want) ? want : p.options.Style[0];
  const si = p.opt.indexOf('Style'), inStyle = p.variants.filter(v => si < 0 || v[1][si] === style), first = inStyle.find(v => v[3]) || inStyle[0];
  sel = Object.fromEntries(p.opt.map((key, k) => [key, key === 'Style' ? style : first ? first[1][k] : p.options[key][0]]));
  big.style.setProperty('--c', p.c);
  big.style.setProperty('--c2', p.c2);
  big.classList.toggle('pd-is-unknown', p.num == null);
  $('dScan').hidden = p.num != null;
  $('dNoTag').textContent = dexNo(p) + (p.form && p.form !== 'New' ? ' · ' + p.form : '');
  $('dWhere').textContent = `${REGIONS[p.region].name} · Gen ${REGIONS[p.region].gen}`;
  $('dPos').textContent = list.length > 1 ? `${at + 1} / ${list.length}` : '';
  $('dPrev').hidden = $('dNext').hidden = list.length < 2;
  $('dName').textContent = p.title; // plain text: the detail is the place to buy, not a way to the product page (owner, 2026-10-15)
  $('dSub').textContent = p.sub || (p.form ? `${p.form} form` : '');
  $('dSub').hidden = !$('dSub').textContent;
  $('dTypes').innerHTML = p.types.map(typePill).join('');
  $('dTypes').hidden = !p.types.length;
  $('dFacts').innerHTML = p.category ? [['Category', p.category], ['Height', p.height], ['Weight', p.weight]].map(([k, v]) => `<div><dt>${k}</dt><dd>${esc(v)}</dd></div>`).join('') : '';
  $('dFacts').hidden = !p.category;
  $('dDesc').textContent = p.desc || `We haven't written this one's dex entry yet. ${p.views.length > 1 ? 'The photos show' : 'The photo shows'} the real print.`;
  const chipFocus = $('dForms').contains(document.activeElement); // rebuilding the chips drops their focus out of the dialog
  $('dForms').innerHTML = p.forms.length ? `<span>Also at ${dexNo(p)}:</span>` + p.forms.map(f =>
    `<button type="button" data-i="${f.i}"><span><img src="${img(f.front, 80)}" alt=""></span>${esc(f.name)}</button>`).join('') : '';
  $('dForms').hidden = !p.forms.length;
  if (chipFocus) ($('dForms').querySelector('button') || $('dClose')).focus();
  $('dOpts').innerHTML = shownOpts(p).map(key =>
    `<fieldset class="pd-seg"><legend>${OPT_LABEL[key] || esc(key)}</legend>${p.options[key].map(v =>
      `<label><input type="radio" name="pd-o-${esc(key)}" value="${esc(v)}"${sel[key] === v ? ' checked' : ''}><span>${key === 'Style' && v === 'Shiny' ? '<i class="pd-star" aria-hidden="true">✦</i>' : ''}${esc(v)}</span></label>`).join('')}</fieldset>`).join('');
  syncOpts();
  const deal = dealOf(p);
  $('dDeal').textContent = deal ? deal[0] : '';
  if (deal) $('dDeal').href = DATA.links[deal[1]];
  // the sizes it comes in ("70mm + Clicker" is the 70mm with a clicker, not another size)
  $('dSize').textContent = p.options.Size ? [...new Set(p.options.Size.map(s => s.split(' ')[0]))].join(' or ') : '70mm';
  $('near').hidden = list.length < 2;
  [['nPrev', -1], ['nNext', 1]].forEach(([id, d]) => {
    const n = DEX[list[(at + d + list.length) % list.length]], b = $(id);
    b.querySelector('img').src = img(n.front, 120);
    b.querySelector('small').textContent = d < 0 ? '‹ ' + dexNo(n) : dexNo(n) + ' ›';
    b.querySelector('b').textContent = n.title;
    b.setAttribute('aria-label', `${d < 0 ? 'Previous' : 'Next'}: ${n.name}, ${dexNo(n)}`);
  });
  // stepping keeps the focus where it is, so say which ball is up now (opening is announced by the dialog's name)
  $('dLive').textContent = dlg.open ? `${p.full}, ${dexNo(p)}, ${at + 1} of ${list.length}` : '';
  resetAdded();
  refresh();
}
// the parts that follow the chosen options: photos, frame, honest photo note, price, the clicker chip, the button
function refresh() {
  // the shiny photos when Shiny is picked and the ball has them (and for a ball that only has shiny photos)
  const p = cur, sh = sel.Style === 'Shiny', pv = ponchoViews(p, sel);
  const set = pv.length ? pv : (sh && p.shinyViews.length) || !p.views.length ? p.shinyViews : p.views;
  const isShinyPhoto = set === p.shinyViews, [shot, f] = set.find(x => x[0] === view) || set[0], src = img(f, 1000);
  if (dImg.getAttribute('src') !== src) { dImg.classList.add('pd-is-loading'); dImg.src = src; }
  dImg.alt = `${isShinyPhoto ? 'Shiny ' : ''}${p.full}${set.length > 1 ? ', ' + shot : ''}${sh && !isShinyPhoto ? ', shown in its normal colours' : ''}`;
  // the strip is rebuilt only when the photo set changes (another ball, Normal / Shiny); a pick just moves the pressed state
  if (strip !== set) {
    strip = set;
    views.innerHTML = set.map(([w, u]) => `<button type="button" data-v="${w}"><span><img src="${img(u, 160)}" alt="" width="160" height="160"></span>${VIEW_LABEL[w]}</button>`).join('');
    views.hidden = set.length < 2;
  }
  views.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', b.dataset.v === shot));
  big.classList.toggle('pd-is-shiny', sh);
  $('dBadge').hidden = !isShinyPhoto;
  // honest notes wherever the photos don't show the chosen option
  const these = set.length > 1 ? 'These photos show' : 'This photo shows';
  const note = [
    sh && !isShinyPhoto && `✦ Shiny print: no shiny photos yet, so ${these.toLowerCase()} the normal print. Yours comes in its shiny colours.`,
    !sh && isShinyPhoto && `${these} the shiny print. The Normal style comes in its standard colours.`,
    sel.Form && sel.Form !== 'Battle Bond' && `${these} the Battle Bond form. The Normal form is printed in its standard look.`,
    sel.Type && sel.Type !== 'Normal' && !pv.length && `${these} the plain ${p.title} ball. The poncho version (${sel.Type.replace('Poncho - ', '')}) is printed with the poncho.`,
    sel.Eeveelution && !pv.length && (p.title === 'Eevee' ? `${these} the plain Eevee ball. Yours is printed with the ${sel.Eeveelution} poncho.`
      : sel.Eeveelution !== 'Umbreon' && `${these} the Umbreon poncho. Yours is printed with the ${sel.Eeveelution} poncho.`)
  ].filter(Boolean).join(' ');
  $('dNote').textContent = note;
  $('dNote').hidden = !note;
  const v = variantOf(p, sel);
  $('dPrice').classList.toggle('pd-is-range', !v);
  $('dPrice').innerHTML = v ? money(v[2]) : `${range(p.price)}<small>not offered in this combination</small>`;
  // the real price step to the Clicker version of what's chosen; a size with no Clicker (100mm) says so instead. The clicker is
  // a size value ("70mm + Clicker" next to "70mm") since the fold, or the older separate Function option
  const fold = !p.options.Function && (p.options.Size || []).includes('70mm + Clicker');
  const vc = fold ? variantOf(p, { ...sel, Size: '70mm + Clicker' }) : p.options.Function && variantOf(p, { ...sel, Function: 'Clicker' });
  const vn = fold ? variantOf(p, { ...sel, Size: '70mm' }) : p.options.Function && variantOf(p, { ...sel, Function: 'Non-Clicker' });
  const extra = vc && vn ? vc[2] - vn[2] : 0, noClick = fold ? sel.Size === '100mm' : !!vn && !vc && p.options.Function.includes('Clicker');
  $('dClick').textContent = noClick ? 'Clicker comes in 70mm only' : 'Clicker option +' + (extra % 100 ? money(extra) : whole.format(extra / 100));
  $('dClick').hidden = !(extra > 0) && !noClick;
  // bundles and the $7 build-your-own are 70mm only, so the deal link hides while 100mm is picked
  $('dDeal').hidden = !dealOf(p) || sel.Size === '100mm';
  setAtc();
}
// the option rows in display order (a product without options has one "Title: Default Title"; nothing to pick there)
function shownOpts(p) { return p.opt.filter(key => p.options[key].join() !== 'Default Title').sort((a, b) => OPT_ORDER.indexOf(a) - OPT_ORDER.indexOf(b)); }
// radios follow sel; a value no variant has with the options shown before it is greyed (Clicker while 100mm is picked), as the crossover hub does
function syncOpts() {
  const p = cur, keys = shownOpts(p), ix = key => p.opt.indexOf(key);
  $('dOpts').querySelectorAll('input').forEach(r => {
    const key = r.name.slice(5), before = keys.slice(0, keys.indexOf(key));
    r.checked = sel[key] === r.value;
    r.disabled = !p.variants.some(v => v[1][ix(key)] === r.value && before.every(k => v[1][ix(k)] === sel[k]));
  });
}
function setAtc() {
  const v = variantOf(cur, sel);
  atc.disabled = !v || !v[3];
  atc.setAttribute('aria-disabled', busy); // while an add is in flight; not .disabled, which would drop the focus out of the dialog
  atc.textContent = !v ? 'Unavailable' : !v[3] ? 'Sold out' : busy ? 'Adding…' : atc.classList.contains('pd-is-added') ? 'Added to cart ✓'
    : atc.classList.contains('pd-is-error') ? "Couldn't add it. Try again" : 'Add to cart';
}
function resetAdded() { clearTimeout(addedTimer); atc.classList.remove('pd-is-added', 'pd-is-error'); $('dAdded').textContent = ''; }
$('dOpts').onchange = e => {
  const p = cur, key = e.target.name.slice(5), k = p.opt.indexOf(key);
  sel[key] = e.target.value;
  // a pick no variant has (100mm with Clicker on) keeps the value just picked and moves the others to the nearest real variant
  if (!variantOf(p, sel)) {
    const score = v => v[1].filter((x, i) => x === sel[p.opt[i]]).length + (v[3] ? .5 : 0);
    const best = p.variants.filter(v => v[1][k] === sel[key]).sort((a, b) => score(b) - score(a))[0];
    if (best) p.opt.forEach((o, i) => { sel[o] = best[1][i]; });
  }
  syncOpts();
  resetAdded();
  refresh();
};
views.onclick = e => { const b = e.target.closest('button'); if (b) { view = b.dataset.v; refresh(); } };
$('dForms').onclick = e => {
  const b = e.target.closest('button[data-i]');
  if (!b) return;
  const i = +b.dataset.i;
  if (!list.includes(i)) list.splice(at + 1, 0, i);
  show(list.indexOf(i));
};
$('dPrev').onclick = $('nPrev').onclick = () => show(at - 1);
$('dNext').onclick = $('nNext').onclick = () => show(at + 1);
$('dClose').onclick = () => dlg.close();
dlg.onclick = e => { if (e.target === dlg) dlg.close(); }; // a click on the backdrop lands on the dialog itself
// on document, capturing: a click on the photo or text drops focus to <body>, where a listener on the dialog never hears the keys
document.addEventListener('keydown', e => {
  if (!dlg.open) return;
  // close here and claim the key: Horizon's docked cart drawer closes on any Escape nobody has claimed (and then keeps this open)
  if (e.key === 'Escape') { e.preventDefault(); dlg.close(); return; }
  if (e.target.matches('input')) return; // arrow keys move between the option radios there
  // on the photo strip, left / right move between its photos instead of to another ball
  const tb = e.target.closest('#pd-dViews button'), d = { ArrowLeft: -1, ArrowRight: 1 }[e.key];
  if (tb && d) {
    const bs = [...views.children], b = bs[(bs.indexOf(tb) + d + bs.length) % bs.length];
    e.preventDefault();
    b.click();
    b.focus();
    return;
  }
  if (e.key === 'ArrowLeft' && list.length > 1) show(at - 1);
  if (e.key === 'ArrowRight' && list.length > 1) show(at + 1);
}, true);
dlg.onclose = () => {
  document.documentElement.classList.remove('pd-lock');
  // focus goes back to the card of the ball you ended on (it scrolls into view if you stepped away)
  const c = cardOf(cur.i);
  if (opener === spinBtn) loadReel(reelPicks.at(-1)); // next spin starts on the ball it landed on
  (opener !== spinBtn && c && !c.parentElement.hidden ? c : opener).focus();
};
grid.onclick = e => {
  const c = e.target.closest('.pd-card');
  if (!c || e.ctrlKey || e.metaKey || e.shiftKey) return; // Ctrl/Cmd/Shift-click opens the product page in a new tab or window
  e.preventDefault();
  open(+c.dataset.i, c);
};

/* ---- add to cart: the same event the theme's own add-to-cart fires, so the header bubble and the cart drawer refresh ---- */
function flash(p, cls, message, v) {
  const err = cls === 'pd-is-error';
  // a failure must be seen: closed meanwhile, the detail opens again on that ball; on another ball, its status line says it (the message names the ball)
  if (err && !dlg.open) open(p.i, cardOf(p.i) || opener);
  else if (cur !== p) { if (err) $('dAdded').textContent = message; return; } // only a success is safe to drop: the cart is updated either way
  $('dAdded').textContent = message;
  if (variantOf(cur, sel) !== v) return; // other options were picked meanwhile: the status names what was added, the button is theirs
  atc.classList.add(cls);
  setAtc();
  clearTimeout(addedTimer);
  addedTimer = setTimeout(() => { atc.classList.remove(cls); setAtc(); }, cls === 'pd-is-added' ? 2000 : 2600);
}
atc.onclick = () => {
  const p = cur, v = variantOf(p, sel);
  if (busy || !v || !v[3]) return;
  busy = true;
  setAtc();
  const opts = [...$('dOpts').querySelectorAll(':checked')].map(r => r.value).join(', ');
  // the ball is in the cart once this resolves; cart is null when only the refresh failed, which loses just the count
  addToCart(atc, [{ id: v[0], quantity: 1 }], 'pokedex-page')
    .then(cart => flash(p, 'pd-is-added', `Added ${p.full} (${opts}) at ${money(v[2])}.` + (cart ? ` Cart: ${plural(cart.item_count, 'item')}.` : ''), v),
      err => flash(p, 'pd-is-error', `Couldn't add ${p.full} (${opts}) to your cart: ${err.message}`, v))
    .finally(() => { busy = false; setAtc(); });
};

/* ---- hero orbit ---- */
const hero = $('hero'), orbs = [...root.querySelectorAll('.pd-orb')];
const pool = DEX.filter(p => p.form !== 'Poncho');
const onOrbit = ['charizard-ball', 'kyogre-ball', 'mimikyu-ball'].map(h => byHandle(h) || pool[0]);
function setOrb(orb, p, shiny) {
  if (!p) return; // an empty collection: nothing to put on the orbit
  const [a, b] = orb.querySelectorAll('img'), next = a.classList.contains('pd-is-hidden') ? a : b, prev = next === a ? b : a;
  shiny = shiny && !!p.shinyFront;
  next.onload = () => { next.classList.remove('pd-is-hidden'); prev.classList.add('pd-is-hidden'); orb.classList.toggle('pd-is-shiny', shiny); };
  next.src = img(shiny ? p.shinyFront : p.front, 300);
}
orbs.forEach((o, k) => setOrb(o, onOrbit[k], k === 1));
let orbTurn = 0;
if (!REDUCED) setInterval(() => {
  if (hero.classList.contains('pd-is-paused') || root.classList.contains('pd-is-still') || document.hidden || dlg.open) return;
  const k = orbTurn++ % 3, choices = pool.filter(p => !onOrbit.includes(p)), p = choices[Math.random() * choices.length | 0];
  if (!p) return; // no ball left to rotate in (three or fewer)
  onOrbit[k] = p;
  setOrb(orbs[k], p, !!p.shinyFront && Math.random() < .5);
}, 3400);
// "Pause motion" (WCAG 2.2.2): the CSS loops and the photo swap above stop while .pd-is-still is on; the label carries the state
const motion = $('motion');
motion.onclick = () => { motion.textContent = root.classList.toggle('pd-is-still') ? 'Play motion' : 'Pause motion'; };

/* ---- "Surprise me" ---- */
const reel = $('reel'), spinBtn = $('spin');
let reelPicks = [], spinning = false;
// only the photo in the window loads with the page; the other 12 load on the first hover, focus or touch
function loadReel(first) {
  if (!DEX.length) return; // an empty collection: the reel stays empty and Surprise me does nothing
  reelPicks = Array.from({ length: 13 }, (_, k) => k === 0 && first ? first : DEX[Math.random() * DEX.length | 0]);
  reel.style.transition = 'none';
  reel.style.transform = 'none';
  reel.innerHTML = `<img src="${img(reelPicks[0].front, 100)}" alt="">`;
}
const fillReel = () => { if (reel.children.length < reelPicks.length) reel.innerHTML = reelPicks.map(p => `<img src="${img(p.front, 100)}" alt="">`).join(''); };
['pointerenter', 'pointerdown', 'focus'].forEach(t => spinBtn.addEventListener(t, fillReel));
loadReel(byHandle('bulbasaur-ball'));
spinBtn.onclick = () => {
  const p = reelPicks.at(-1);
  if (spinning || !p) return;
  if (REDUCED) return open(p.i, spinBtn, true);
  spinning = true;
  fillReel(); // a keyboard or scripted click may come without a hover first
  void reel.offsetWidth;
  reel.style.transition = 'transform 1.4s cubic-bezier(.12,.8,.22,1.04)';
  reel.style.transform = `translateY(${-(reelPicks.length - 1) / reelPicks.length * 100}%)`;
  // a ball the shopper opened during the spin stays open; the reel just resets on where it landed
  setTimeout(() => { spinning = false; if (dlg.open) loadReel(p); else open(p.i, spinBtn, true); }, 1550);
};

// loops pause while off screen: the hero emblem and the numberless cards' scanners
const io = new IntersectionObserver(es => es.forEach(e => e.target.classList.toggle('pd-is-paused', !e.isIntersecting)));
io.observe(hero);
grid.querySelectorAll('.pd-is-unknown').forEach(c => io.observe(c));

// a group link from another page (/collections/pokedex#legendary) opens with that group picked
const hashGroup = GROUPS.find(g => g.id === location.hash.slice(1));
if (hashGroup) state.group = hashGroup.id;
applyLook(false);
apply(false);
if (hashGroup) $('dex').scrollIntoView();
root.classList.add('pd-ready'); // the section's fallback script checks this on load
